import React, { useState, useEffect, useCallback } from "react";
import T from "../styles/theme";
import Icon from "../components/Icon";
import { ONBOARDING_CHECKLIST, CHECKLIST_CATEGORIES, PresetIcon } from "../data/CompanyPresets";

/* ═══════════════════════════════════════════════════════
   SETUP CHECKLIST PAGE
   Full-page onboarding progress tracker
   Disappears from nav once all items complete
   ═══════════════════════════════════════════════════════ */

export function isSetupComplete() {
  try {
    const checked = JSON.parse(localStorage.getItem("cf_onboarding_checklist") || "{}");
    return ONBOARDING_CHECKLIST.every(item => checked[item.id]);
  } catch { return false; }
}

export function getSetupProgress() {
  try {
    const checked = JSON.parse(localStorage.getItem("cf_onboarding_checklist") || "{}");
    const completed = ONBOARDING_CHECKLIST.filter(item => checked[item.id]).length;
    return { completed, total: ONBOARDING_CHECKLIST.length, pct: Math.round((completed / ONBOARDING_CHECKLIST.length) * 100) };
  } catch { return { completed: 0, total: ONBOARDING_CHECKLIST.length, pct: 0 }; }
}

export default function SetupChecklistPage({ setPage }) {
  const [checkedItems, setCheckedItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cf_onboarding_checklist") || "{}"); }
    catch { return {}; }
  });

  const save = useCallback((items) => {
    setCheckedItems(items);
    localStorage.setItem("cf_onboarding_checklist", JSON.stringify(items));
  }, []);

  const toggle = useCallback((id) => {
    const next = { ...checkedItems, [id]: !checkedItems[id] };
    save(next);
  }, [checkedItems, save]);

  const total = ONBOARDING_CHECKLIST.length;
  const completed = ONBOARDING_CHECKLIST.filter(item => checkedItems[item.id]).length;
  const pct = Math.round((completed / total) * 100);

  // Group by category
  const grouped = {};
  ONBOARDING_CHECKLIST.forEach(item => {
    if (!grouped[item.category]) grouped[item.category] = [];
    grouped[item.category].push(item);
  });

  const cardStyle = {
    background: T.sf,
    backdropFilter: "blur(20px) saturate(1.4)",
    WebkitBackdropFilter: "blur(20px) saturate(1.4)",
    borderRadius: 8,
    border: "1px solid " + T.bdr,
    overflow: "hidden",
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 4px" }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 400, color: T.txt, marginBottom: 6 }}>
          Setup Your CRM
        </div>
        <div style={{ fontSize: 13, color: T.tx3, lineHeight: 1.5, marginBottom: 20 }}>
          Complete these steps to get the most out of Coreflow. You can do them in any order.
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ flex: 1, height: 8, borderRadius: 8, background: T.bdr, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 8,
              background: "#4059AD",
              width: `${pct}%`,
              transition: "width .6s cubic-bezier(.4,0,.2,1)",
            }} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 400, color: pct === 100 ? "#FAFAFA" : "#FAFAFA", minWidth: 42, textAlign: "right" }}>
            {pct}%
          </div>
        </div>
        <div style={{ fontSize: 11, color: T.tx3, marginTop: 6 }}>
          {completed} of {total} complete
        </div>
      </div>

      {/* All done banner */}
      {pct === 100 && (
        <div style={{
          ...cardStyle, padding: "24px 28px", marginBottom: 24,
          background: "rgba(64,89,173,0.04)", border: "1px solid rgba(64,89,173,0.15)",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA", marginBottom: 6 }}>
            You're all set!
          </div>
          <div style={{ fontSize: 13, color: T.tx2, marginBottom: 16 }}>
            Your CRM is fully configured and ready to go.
          </div>
          <button
            onClick={() => setPage("dashboard")}
            style={{
              padding: "10px 28px", borderRadius: 8,
              background: "#4059AD", color: "#D9DBDD", border: "none",
              fontSize: 13, fontWeight: 400, cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Go to Mission Control
          </button>
        </div>
      )}

      {/* Category sections */}
      {Object.entries(grouped).map(([catKey, items]) => {
        const cat = CHECKLIST_CATEGORIES[catKey];
        const catDone = items.every(item => checkedItems[item.id]);
        const catCount = items.filter(item => checkedItems[item.id]).length;

        return (
          <div key={catKey} style={{ ...cardStyle, marginBottom: 14 }}>
            {/* Category header */}
            <div style={{
              padding: "14px 20px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderBottom: "1px solid " + T.bdr,
              background: catDone ? "rgba(0,214,143,0.04)" : "transparent",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: catDone ? "rgba(0,214,143,0.15)" : "#4059AD",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <PresetIcon name={cat?.icon} size={16} color={catDone ? "#00D68F" : "#FAFAFA"} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{cat?.label}</div>
                  <div style={{ fontSize: 10, color: T.tx3 }}>{catCount}/{items.length} complete</div>
                </div>
              </div>
            </div>

            {/* Items */}
            <div style={{ padding: "6px 8px" }}>
              {items.map((item, idx) => {
                const done = !!checkedItems[item.id];
                return (
                  <div
                    key={item.id}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 12,
                      padding: "12px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "background .15s",
                      borderBottom: idx < items.length - 1 ? "1px solid " + T.bdr + "60" : "none",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = T.blue + "04"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    onClick={() => {
                      if (!done && item.route && setPage) {
                        // Navigate to the relevant page
                        setPage(item.route);
                      }
                      toggle(item.id);
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{
                      width: 20, height: 20, borderRadius: 8, flexShrink: 0, marginTop: 2,
                      border: `1.5px solid ${done ? "#4059AD" : "rgba(217,219,221,0.12)"}`,
                      background: done ? "#4059AD" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all .15s",
                    }}>
                      {done && <Icon name="check" size={12} color="#FAFAFA" />}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 400, color: done ? T.tx3 : T.txt,
                        textDecoration: "none",
                        lineHeight: 1.3, position: "relative", display: "inline-block",
                      }}>
                        {item.label}
                        {done && <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: T.tx3 }} />}
                      </div>
                      <div style={{
                        fontSize: 11, color: T.tx3, lineHeight: 1.4, marginTop: 2,
                        opacity: done ? 0.5 : 0.8,
                      }}>
                        {item.description}
                      </div>
                      {!done && item.note && (
                        <div style={{
                          fontSize: 10, color: "#FAFAFA", marginTop: 4,
                          fontWeight: 400, lineHeight: 1.3,
                        }}>
                          {item.note}
                        </div>
                      )}
                    </div>

                    {/* Arrow for navigable items */}
                    {!done && item.route && (
                      <div style={{ flexShrink: 0, marginTop: 4 }}>
                        <Icon name="chevronR" size={14} color={T.tx3} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Reset option */}
      <div style={{ textAlign: "center", padding: "16px 0 32px" }}>
        <button
          onClick={() => {
            save({});
            localStorage.removeItem("cf_checklist_dismissed");
          }}
          style={{
            background: "none", border: "none",
            fontSize: 10, color: T.tx3, cursor: "pointer",
            fontFamily: "inherit", textDecoration: "underline",
            opacity: 0.5,
          }}
        >
          Reset checklist
        </button>
      </div>
    </div>
  );
}
